/**
 * API Headers Utility (Enterprise Standard - Bearer Token Only)
 *
 * CANONICAL TOKEN STORAGE KEY: "pathkit_session_token"
 * 
 * UPDATED FOR ENTERPRISE AUTH (Feb 26, 2026):
 * - Uses ONLY standard Authorization: Bearer <token> header
 * - NO custom headers (x-session-token removed)
 * - NO cookies (credentials: 'include' removed)
 * - Minimal CORS surface for maximum reliability
 *
 * Contract:
 *   - Gateway auth:  Authorization: Bearer <SUPABASE_ANON_KEY> (for public endpoints)
 *   - Session auth:  Authorization: Bearer <sessionToken> (for authenticated endpoints)
 *   - apikey header: <SUPABASE_ANON_KEY>
 *
 * Usage:
 *   import { getAuthHeaders, getFetchOptions } from '../utils/apiHeaders';
 *
 *   // Public endpoint (no session)
 *   fetch(url, getFetchOptions());
 *
 *   // Authenticated endpoint
 *   const sessionToken = getStoredSessionToken();
 *   fetch(url, getFetchOptions('GET', null, sessionToken));
 *
 *   // POST with JSON body
 *   fetch(url, getFetchOptions('POST', { data: 'value' }, sessionToken));
 */

import { publicAnonKey } from './supabase/info';

// CANONICAL TOKEN STORAGE KEY - DO NOT CHANGE
export const SESSION_TOKEN_KEY = 'pathkit_session_token';

/**
 * Get stored session token from localStorage
 * Returns null if no valid session exists
 * 
 * DIAGNOSTIC: Logs token presence (not value)
 */
export function getStoredSessionToken(): string | null {
  try {
    const token = localStorage.getItem(SESSION_TOKEN_KEY);
    
    // Diagnostic log (DO NOT log token value)
    console.log(`[Auth] getStoredSessionToken: tokenPresent=${!!token}, keyLength=${token?.length || 0}`);
    
    if (!token) {
      console.log(`[Auth] No token found in localStorage key: ${SESSION_TOKEN_KEY}`);
      return null;
    }
    
    // Validate token format (should start with sess_ or imp_)
    if (!token.startsWith('sess_') && !token.startsWith('imp_')) {
      console.warn(`[Auth] Invalid session token format in localStorage (expected sess_* or imp_*)`);
      localStorage.removeItem(SESSION_TOKEN_KEY);
      return null;
    }
    
    return token;
  } catch (error) {
    console.error('[Auth] Error reading session token:', error);
    return null;
  }
}

/**
 * Store session token in localStorage
 * DIAGNOSTIC: Logs storage success
 */
/**
 * The signed-in caller's bearer token, or null when nobody is signed in.
 * Order: the `sess_`/`imp_` session token (two storage keys hold the same
 * value), then a Supabase JWT. Anything else in storage is ignored.
 */
export function getBearerToken(): string | null {
  try {
    const sess = localStorage.getItem(SESSION_TOKEN_KEY) || localStorage.getItem('pathkit_session_id');
    if (sess && (sess.startsWith('sess_') || sess.startsWith('imp_'))) return sess;
    const jwt = localStorage.getItem('pathkit_access_token');
    if (jwt && jwt.split('.').length === 3) return jwt;
  } catch {
    // storage unavailable (private mode, SSR): treated as signed out
  }
  return null;
}

/**
 * The Authorization header for a server call: the caller's token when signed
 * in, the public anon key otherwise. The anon key identifies nobody, so with
 * it only the server's public allowlist (authFloor.ts) answers. Spread it into
 * a headers object: `headers: { ...bearerAuthHeader(), 'Content-Type': ... }`.
 */
export function bearerAuthHeader(): { Authorization: string } {
  return { Authorization: `Bearer ${getBearerToken() || publicAnonKey}` };
}

export function storeSessionToken(token: string): void {
  try {
    localStorage.setItem(SESSION_TOKEN_KEY, token);
    console.log(`[Auth] Session token stored successfully (length: ${token.length})`);
  } catch (error) {
    console.error('[Auth] Error storing session token:', error);
  }
}

/**
 * Clear session token from localStorage
 * Also clears legacy keys for clean migration
 */
export function clearSessionToken(): void {
  try {
    localStorage.removeItem(SESSION_TOKEN_KEY);
    
    // Legacy cleanup
    localStorage.removeItem('pathkit_admin_session');
    localStorage.removeItem('pk_session');
    localStorage.removeItem('sessionToken'); // Sometimes used by mistake
    
    console.log('[Auth] Session token cleared from localStorage');
  } catch (error) {
    console.error('[Auth] Error clearing session token:', error);
  }
}

/**
 * Returns minimal headers for Supabase Edge Function auth.
 * 
 * If sessionToken is provided, uses it in Authorization: Bearer
 * Otherwise, uses anon key in Authorization: Bearer
 * 
 * DIAGNOSTIC: Logs whether auth is attached
 * 
 * IMPORTANT: NO custom headers, NO credentials: 'include'
 */
export function getAuthHeaders(sessionToken?: string | null): Record<string, string> {
  const token = sessionToken || publicAnonKey;
  const isSessionToken = sessionToken && (sessionToken.startsWith('sess_') || sessionToken.startsWith('imp_'));
  
  // Diagnostic log (DO NOT log token value)
  console.log(`[Auth] getAuthHeaders: authAttached=${isSessionToken}, usingSessionToken=${isSessionToken}`);
  
  return {
    'Authorization': `Bearer ${token}`,
    'apikey': publicAnonKey,
  };
}

/**
 * Returns headers with Content-Type: application/json
 */
export function getAuthHeadersWithJson(sessionToken?: string | null): Record<string, string> {
  return {
    ...getAuthHeaders(sessionToken),
    'Content-Type': 'application/json',
  };
}

/**
 * Returns complete fetch options with proper headers.
 * This is the RECOMMENDED way to make API calls.
 * 
 * ALWAYS auto-fetches token from localStorage unless explicitly overridden.
 * 
 * @param method - HTTP method (default: 'GET')
 * @param body - Request body (will be JSON.stringify'd if provided)
 * @param sessionToken - Session token (if undefined, reads from localStorage; if null, uses anon key only)
 * @param extraHeaders - Additional headers to merge
 */
export function getFetchOptions(
  method: string = 'GET',
  body?: any,
  sessionToken?: string | null,
  extraHeaders?: Record<string, string>
): RequestInit {
  // CRITICAL: Auto-fetch session token from localStorage if not provided
  // If caller passes null explicitly, that means "no session" (anon only)
  // If caller passes undefined (or nothing), auto-read from storage
  let token: string | null = null;
  
  if (sessionToken === undefined) {
    token = getStoredSessionToken();
    console.log(`[Auth] getFetchOptions: auto-loaded token from localStorage, tokenPresent=${!!token}`);
  } else {
    token = sessionToken;
    console.log(`[Auth] getFetchOptions: using provided token, tokenPresent=${!!token}`);
  }
  
  const headers = body 
    ? getAuthHeadersWithJson(token) 
    : getAuthHeaders(token);

  const options: RequestInit = {
    method,
    headers: { ...headers, ...extraHeaders },
    // CRITICAL: NO credentials: 'include' (enterprise standard - bearer token only)
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  return options;
}